import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';

const WIX_EVENTS_APP_DEF_ID = '140603ad-af8d-84a5-2c80-a0f60cb47351';
const WIX_EVENTS_CHECKOUT_WIDGET_ID = '1440e92d-47d8-69be-ade7-e6de40127106';
const LEGACY_EVENTS_CHECKOUT_SITE_COLOR_NAME = 'color_13';
const LEGACY_EVENTS_CHECKOUT_TARGET = 'color_39';

const getCompRef = (editorAPI: EditorAPI, compRefOrId: string | CompRef) =>
  typeof compRefOrId === 'string'
    ? editorAPI.dsRead.components.get.byId(compRefOrId)
    : compRefOrId;

const getCompData = (editorAPI: EditorAPI, compRefOrId: string | CompRef) => {
  const compRef = getCompRef(editorAPI, compRefOrId);

  if (!compRef) {
    return null;
  }

  return editorAPI.components.data.get(compRef);
};

const isLegacyEventsCheckoutComp = (
  editorAPI: EditorAPI,
  compRefOrId: string | CompRef,
) => {
  const compData = getCompData(editorAPI, compRefOrId);

  return (
    compData &&
    (compData.appDefinitionId === WIX_EVENTS_APP_DEF_ID ||
      compData.widgetId === WIX_EVENTS_CHECKOUT_WIDGET_ID)
  );
};

const findLegacyEventsCheckoutComp = (editorAPI: EditorAPI) => {
  const allComponents = editorAPI.components.getAllComponents();

  return (
    allComponents.find((compRef) =>
      isLegacyEventsCheckoutComp(editorAPI, compRef),
    ) ?? null
  );
};

const getLinkedColors = (editorAPI: EditorAPI) =>
  editorAPI.theme.colors.getAllLinkedColors() ?? {};

const refreshLegacyEventsCheckoutLivePreview = (
  editorAPI: EditorAPI,
  compRefOrId: string | CompRef,
) => {
  const compData = getCompData(editorAPI, compRefOrId);
  const appDefinitionId = compData?.appDefinitionId ?? WIX_EVENTS_APP_DEF_ID;

  editorAPI.dsActions.platform?.livePreview?.refresh?.({
    apps: [appDefinitionId],
    source: 'legacyEventsCheckoutColor',
    shouldFetchData: true,
  });
};

const syncLegacyEventsCheckoutThemeColorForAll = async (
  editorAPI: EditorAPI,
) => {
  const compRef = findLegacyEventsCheckoutComp(editorAPI);

  if (!compRef) {
    return false;
  }

  const linkedColors = getLinkedColors(editorAPI);
  const targetColor =
    linkedColors[LEGACY_EVENTS_CHECKOUT_TARGET] ??
    LEGACY_EVENTS_CHECKOUT_TARGET;

  if (linkedColors[LEGACY_EVENTS_CHECKOUT_SITE_COLOR_NAME] === targetColor) {
    return false;
  }

  editorAPI.theme.colors.update({
    [LEGACY_EVENTS_CHECKOUT_SITE_COLOR_NAME]: targetColor,
  });
  await editorAPI.waitForChangesAppliedAsync();
  refreshLegacyEventsCheckoutLivePreview(editorAPI, compRef);

  return true;
};

export { syncLegacyEventsCheckoutThemeColorForAll };
